/**
 * /execution/install-today — Install Today Operational Layer
 * ──────────────────────────────────────────────────────────
 * Single operator landing surface that consolidates the live install board,
 * scheduling risks, blocking issues, and clustered escalation signals into
 * one cockpit page. Quick Actions footer surfaces deep links only.
 *
 * Strict invariants (Execution Domain lock + scope spec):
 *  1. RBAC: super_admin / admin / ops_manager (canonical) + head_of_installation
 *     (per-route operational exception, mirrors /execution/installs-live).
 *  2. Read-only over canonical resolvers — no writers, no new sinks.
 *  3. No financial fields (price/pay) referenced anywhere.
 *  4. No PM-attributed analytics (none rendered on this page).
 *  5. MIN_SAMPLE_FOR_DISPLAY enforced for cluster signals.
 *  6. Cockpit primitives only.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { ChecklistReviewSheet } from "@/components/execution/ChecklistReviewSheet";
import {
  AlertTriangle, Clock,
  LifeBuoy, ShieldCheck, XCircle, Users,
  RefreshCw, Layers, Activity, Siren,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import {
  CockpitTabs,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  StatusDot,
  InlineMeta,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
} from "@/components/cockpit/CockpitShell";
import { SubPivot } from "@/components/cockpit/SubPivot";
import { useAppData } from "@/data/AppDataContext";
import { useRole, type UserRole } from "@/data/RoleContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { resolveMaterialReadiness } from "@/data/procurement/materialReadinessResolver";
import { resolvePermitOperationalSignals } from "@/data/permitting/permitOperationalSignals";
import { getJobBomReconciliation } from "@/data/materialsReconciliation";

const CHECKLIST_LINK_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  "super_admin",
  "admin",
  "ops_manager",
  "head_of_installation",
  "it_staff",
]);
import { canAccessInstallsLive } from "@/data/executionDomain";
import { useScheduling } from "@/data/scheduling/context";
import {
  buildInstallsLiveRows,
  type InstallsLiveRow,
} from "@/data/observability/installsLiveSelector";
import {
  buildEscalationSignals,
  type EscalationSignal,
  type EscalationSeverity,
} from "@/data/observability/escalationSignalsSelector";
import { getJobRiskSignals } from "@/data/scheduling/riskSignals";
import { PermitOperationalChip } from "@/components/permitting/PermitOperationalStrip";
// ProcurementOperationalChip intentionally not used here — Material Readiness is the canonical install-row chip.
import { MaterialReadinessChip } from "@/components/procurement/MaterialReadinessChip";
import type { Job } from "@/data/types";
import { cn } from "@/lib/utils";
import {
  classifyPostScheduleBlock,
  type PostScheduleBlockVerdict,
} from "@/data/scheduling/postScheduleBlockClassifier";
import {
  resolveInstallTodayActions,
  type InstallAction,
} from "@/data/execution/installTodayActions";
import { openResolutionDrawer } from "@/data/execution/resolutionDrawerBus";
import { UnifiedResolutionDrawer } from "@/components/execution/UnifiedResolutionDrawer";

import { JobTriageSheet } from "@/components/execution/JobTriageSheet";
import { Link } from "react-router-dom";
import { EXECUTION_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isExecutionServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import ExecutionInstallTodayServerPage from "./ExecutionInstallTodayServerPage";

type TabKey = "active" | "escalations";

type Lifecycle = "in_progress" | "ready" | "blocked";
interface RowMeta {
  lifecycle: Lifecycle;
  blockerReasons: string[];
  blockCause: PostScheduleBlockVerdict;
}

const TAB_LABELS: Record<TabKey, string> = {
  active: "Active Installs",
  escalations: "Escalation Signals",
};

const TAB_ORDER: TabKey[] = ["active", "escalations"];

/* ── tab key ↔ url alias (active-installs reads cleaner in URLs / bookmarks) ──
 * Legacy aliases (scheduling-risks, blocking-issues) silently fall back to
 * "active" — those concerns now live on Job Detail and inline per-row. */
const TAB_TO_URL: Record<TabKey, string> = {
  active: "active-installs",
  escalations: "escalation-signals",
};
const URL_TO_TAB: Record<string, TabKey> = Object.fromEntries(
  Object.entries(TAB_TO_URL).map(([k, v]) => [v, k as TabKey]),
) as Record<string, TabKey>;

function fmtAge(iso?: string, now: Date = new Date()): string {
  if (!iso) return "—";
  const ms = now.getTime() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

function severityClass(sev: EscalationSeverity): string {
  if (sev === "high") return "bg-destructive/15 text-destructive ring-1 ring-destructive/30";
  if (sev === "medium") return "bg-warning/15 text-warning ring-1 ring-warning/30";
  return "bg-muted text-muted-foreground ring-1 ring-border/60";
}

function PhaseStatusBadge({ row }: { row: InstallsLiveRow }) {
  if (!row.executionAvailable) {
    return <Badge variant="outline">Unavailable</Badge>;
  }
  if (row.awaitingQa) {
    return (
      <Badge className="bg-primary/15 text-primary ring-1 ring-primary/30 gap-1">
        <ShieldCheck className="h-3 w-3" /> Awaiting QA
      </Badge>
    );
  }
  if (row.reconciliationPending) {
    return (
      <Badge className="bg-warning/15 text-warning ring-1 ring-warning/30 gap-1">
        <AlertTriangle className="h-3 w-3" /> Reconcile
      </Badge>
    );
  }
  if (row.activePhaseStatus === "qa_failed") {
    return <Badge variant="destructive">QA Failed</Badge>;
  }
  if (row.activePhaseStatus === "in_progress") {
    return <Badge variant="outline">In progress</Badge>;
  }
  return <Badge variant="outline">On track</Badge>;
}

/* Tile component removed — KPIs now render inline in the masthead via HeroMetric. */

/**
 * Portals children into the parent CockpitPage's controls slot
 * (`#install-masthead-controls`) so the embedded Execute view's KPIs render
 * on the same line as the "Install" title — matching every other page.
 */
function PortalToControls({ children }: { children: React.ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.getElementById("install-masthead-controls");
    setHost(el);
  }, []);
  if (!host) return null;
  return createPortal(<>{children}</>, host);
}

export default function ExecutionInstallTodayPage(props: { embedded?: boolean } = {}) {
  const location = useBrowserLocationSnapshot();
  if (
    EXECUTION_SERVER_PAGING_ENABLED &&
    isExecutionServerPagedRoute(location.pathname, location.search)
  ) {
    return <ExecutionInstallTodayServerPage {...props} />;
  }
  return <LegacyExecutionInstallTodayPage {...props} />;
}

function LegacyExecutionInstallTodayPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { role } = useRole();
  const { jobs, specExecutions, issues, serviceTickets } = useAppData();
  const { eventLog, auditTrail } = useScheduling();
  const { getPOsByJob, getFieldFeedbackByJob, getSubstitutionRequestsByJob } = useVendorPO();
  const { receivingRecords, jobAllocations } = useWarehouseInventory();
  const { packets } = useOrderingPacket();
  const [params, setParams] = useSearchParams();

  // When embedded, parent owns ?tab=execute; we read ?focus= instead so the
  // inner pivot doesn't collide with the outer tab strip.
  const paramKey = embedded ? "focus" : "tab";
  const tabParamRaw = params.get(paramKey) ?? "";
  const tab: TabKey = URL_TO_TAB[tabParamRaw] ?? "active";

  const now = useMemo(() => new Date(), []);

  const rows = useMemo(
    () => buildInstallsLiveRows({ jobs, specExecutions, now }),
    [jobs, specExecutions, now],
  );

  const riskByJob = useMemo(() => {
    const m = new Map<string, ReturnType<typeof getJobRiskSignals>>();
    for (const r of rows) m.set(r.jobId, getJobRiskSignals(r.jobId, eventLog, auditTrail));
    return m;
  }, [rows, eventLog, auditTrail]);

  const escalations = useMemo(
    () => buildEscalationSignals({ rows, riskByJob }),
    [rows, riskByJob],
  );

  // Per-job active issue + ticket counts (read-only join).
  const activeIssuesByJob = useMemo(() => {
    const m = new Map<string, typeof issues>();
    for (const i of issues) {
      if (i.status === "Resolved" || i.status === "Closed") continue;
      const arr = m.get(i.jobId) ?? [];
      arr.push(i);
      m.set(i.jobId, arr);
    }
    return m;
  }, [issues]);

  const activeTicketsByJob = useMemo(() => {
    const m = new Map<string, typeof serviceTickets>();
    for (const t of serviceTickets) {
      if (t.status === "Resolved" || t.status === "Closed") continue;
      const arr = m.get(t.jobId) ?? [];
      arr.push(t);
      m.set(t.jobId, arr);
    }
    return m;
  }, [serviceTickets]);

  // Risk rows: any in-flight install carrying a risk signal.
  const riskyRows = useMemo(
    () => rows.filter((r) => riskByJob.get(r.jobId)?.hasAny),
    [rows, riskByJob],
  );

  // Blocking rows: any in-flight install with active issues OR active tickets,
  // grouped by job for scan speed.
  const blockingRows = useMemo(() => {
    return rows
      .map((r) => ({
        row: r,
        issues: activeIssuesByJob.get(r.jobId) ?? [],
        tickets: activeTicketsByJob.get(r.jobId) ?? [],
      }))
      .filter((x) => x.issues.length + x.tickets.length > 0)
      .sort((a, b) =>
        (b.issues.length + b.tickets.length) - (a.issues.length + a.tickets.length),
      );
  }, [rows, activeIssuesByJob, activeTicketsByJob]);

  const jobsById = useMemo(() => {
    const m = new Map<string, Job>();
    for (const j of jobs) m.set(j.id, j);
    return m;
  }, [jobs]);

  /* ── Lifecycle classification ────────────────────────────────────
   * Honest, explainable inclusion reason for every row on the Active
   * list. Pure projection over canonical resolvers — no new truth.
   *   in_progress: installer has at least one phase in_progress /
   *                submitted / qa_failed (actually installing today).
   *   blocked    : scheduled today, no installer activity yet, AND
   *                permit.installBlocker OR material.blocker OR any
   *                active issue/ticket holding the job.
   *   ready      : scheduled today, no installer activity yet, and
   *                no blockers — waiting for installer to start.
   * Ordering on render: in_progress → blocked → ready (operator scan
   * priority). The selector's default score within each group is
   * preserved.                                                      */
  const metaByJob = useMemo(() => {
    const m = new Map<string, RowMeta>();
    for (const r of rows) {
      const job = jobsById.get(r.jobId);
      if (!job) continue;
      const inProgress =
        r.executionAvailable &&
        r.phases.some(
          (p) =>
            p.status === "in_progress" ||
            p.status === "submitted" ||
            p.status === "qa_failed",
        );

      if (inProgress) {
        m.set(r.jobId, {
          lifecycle: "in_progress",
          blockerReasons: [],
          blockCause: { cause: "none", label: "", detail: "" },
        });
        continue;
      }

      // Not started yet — evaluate blockers.
      const permit = resolvePermitOperationalSignals(job);
      const fieldFeedback = getFieldFeedbackByJob(job.id);
      const substitutionRequests = getSubstitutionRequestsByJob(job.id);
      const material = resolveMaterialReadiness({
        job,
        purchaseOrders: getPOsByJob(job.id),
        orderingPackets: packets
          .filter((p) => p.jobId === job.id)
          .flatMap((p) => p.lineItems),
        receivingRecords: receivingRecords.filter((rr) => rr.jobId === job.id),
        jobAllocations: jobAllocations.filter((a) => a.jobId === job.id),
        reconciliation: getJobBomReconciliation(job),
        fieldFeedback,
        substitutionRequests,
      });
      const activeIssues = activeIssuesByJob.get(r.jobId) ?? [];
      const activeTickets = activeTicketsByJob.get(r.jobId) ?? [];
      const issueCount = activeIssues.length;
      const ticketCount = activeTickets.length;

      const reasons: string[] = [];
      if (permit.installBlocker) reasons.push(permit.installBlockerReason || "Permit blocker");
      if (material.blocker) reasons.push(material.blockerReason || "Materials not ready");
      if (issueCount > 0) reasons.push(`${issueCount} open issue${issueCount === 1 ? "" : "s"}`);
      if (ticketCount > 0) reasons.push(`${ticketCount} open ticket${ticketCount === 1 ? "" : "s"}`);

      const blockCause = classifyPostScheduleBlock({
        job,
        hasBlocker: reasons.length > 0,
        activeIssues,
        activeTickets,
        unresolvedFieldFeedback: fieldFeedback.filter((e) => !e.resolved),
        openSubstitutionRequests: substitutionRequests.filter(
          (s) => s.status === "pending_review" || s.status === "approved",
        ),
      });

      m.set(r.jobId, {
        lifecycle: reasons.length > 0 ? "blocked" : "ready",
        blockerReasons: reasons,
        blockCause,
      });
    }
    return m;
  }, [
    rows, jobsById, getPOsByJob, packets, receivingRecords, jobAllocations,
    getFieldFeedbackByJob, getSubstitutionRequestsByJob,
    activeIssuesByJob, activeTicketsByJob,
  ]);

  // Per-job counts of unresolved field feedback and open substitutions.
  // Used by the operational action resolver to know whether to surface
  // the "Field Feedback" and "Substitution" deep-links.
  const fieldFeedbackByJob = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const events = getFieldFeedbackByJob(r.jobId).filter((e) => !e.resolved);
      if (events.length > 0) m.set(r.jobId, events.length);
    }
    return m;
  }, [rows, getFieldFeedbackByJob]);

  const substitutionsByJob = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const subs = getSubstitutionRequestsByJob(r.jobId).filter(
        (s) => s.status === "pending_review" || s.status === "approved",
      );
      if (subs.length > 0) m.set(r.jobId, subs.length);
    }
    return m;
  }, [rows, getSubstitutionRequestsByJob]);

  const inProgressRows = useMemo(
    () => rows.filter((r) => metaByJob.get(r.jobId)?.lifecycle === "in_progress"),
    [rows, metaByJob],
  );
  const blockedRows = useMemo(
    () => rows.filter((r) => metaByJob.get(r.jobId)?.lifecycle === "blocked"),
    [rows, metaByJob],
  );
  const readyRows = useMemo(
    () => rows.filter((r) => metaByJob.get(r.jobId)?.lifecycle === "ready"),
    [rows, metaByJob],
  );

  const counts: Record<TabKey, number> = {
    active: rows.length,
    escalations: escalations.length,
  };

  if (!canAccessInstallsLive(role)) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Card className="p-6 text-sm text-muted-foreground">
          You don't have access to Install Today.
        </Card>
      </div>
    );
  }

  const tabs = TAB_ORDER.map((k) => ({
    value: k, label: TAB_LABELS[k], count: counts[k],
  }));

  const setTab = (k: TabKey) => {
    const next = new URLSearchParams(params);
    if (k === "active") next.delete(paramKey);
    else next.set(paramKey, TAB_TO_URL[k]);
    setParams(next, { replace: true });
  };

  // High-level tiles: scan in 3 seconds. Reflect lifecycle reality:
  // "In Progress" = installer is actually working today. "Blocked" and
  // "Ready" surface the rest of the scheduled day honestly.
  const inProgress = inProgressRows.length;
  const blockedCount = blockedRows.length;
  const readyCount = readyRows.length;
  const awaitingQa = rows.filter((r) => r.awaitingQa).length;
  const highSeverity = escalations.filter((e) => e.severity === "high").length;
  const mediumSeverity = escalations.filter((e) => e.severity === "medium").length;
  const lowSeverity = escalations.filter((e) => e.severity === "low").length;

  // Right-aligned masthead KPIs — mirror Pipeline's hero+secondary layout.
  // Per-tab so the headline metric matches the active surface.
  const headerRight =
    tab === "active" ? (
      <>
        <HeroMetric label="In Progress" value={inProgress} hero />
        <HeroDivider />
        <HeroMetric label="Ready" value={readyCount} tone={readyCount > 0 ? "good" : "default"} />
        <HeroDivider />
        <HeroMetric label="Blocked" value={blockedCount} tone={blockedCount > 0 ? "bad" : "default"} />
        <HeroDivider />
        <HeroMetric label="Awaiting QA" value={awaitingQa} tone={awaitingQa > 0 ? "warn" : "default"} />
      </>
    ) : (
      <>
        <HeroMetric label="Signals" value={escalations.length} hero />
        <HeroDivider />
        <HeroMetric label="High" value={highSeverity} tone={highSeverity > 0 ? "bad" : "default"} />
        <HeroDivider />
        <HeroMetric label="Medium" value={mediumSeverity} tone={mediumSeverity > 0 ? "warn" : "default"} />
        <HeroDivider />
        <HeroMetric label="Low" value={lowSeverity} />
      </>
    );

  const tabsNode = (
    <CockpitTabs
      tabs={tabs}
      value={tab}
      onChange={(v) => setTab(v as TabKey)}
    />
  );

  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  const canOpenChecklist = CHECKLIST_LINK_ROLES.has(role);

  const body = (
    <>
      {tab === "active" && (
        <ActiveInstallsTab
          inProgressRows={inProgressRows}
          blockedRows={blockedRows}
          readyRows={readyRows}
          metaByJob={metaByJob}
          now={now}
          activeIssuesByJob={activeIssuesByJob}
          activeTicketsByJob={activeTicketsByJob}
          jobsById={jobsById}
          canOpenChecklist={canOpenChecklist}
          onReviewChecklist={(id) => setReviewJobId(id)}
          role={role}
          fieldFeedbackByJob={fieldFeedbackByJob}
          substitutionsByJob={substitutionsByJob}
        />
      )}
      {tab === "escalations" && <EscalationsTab signals={escalations} />}
      <ChecklistReviewSheet
        jobId={reviewJobId}
        open={reviewJobId !== null}
        onOpenChange={(o) => { if (!o) setReviewJobId(null); }}
      />
    </>
  );

  if (embedded) {
    // Parent (ExecutionDomainLanding) owns the masthead ("Install") and the
    // outer tab strip (Schedule · Execute · Closeout). Portal the KPI cluster
    // up into the parent's masthead controls slot so the SubPivot sits
    // directly beneath the tabs — matching every other cockpit page.
    return (
      <>
        <PortalToControls>{headerRight}</PortalToControls>
        <div className="space-y-4">
          <SubPivot<TabKey>
            value={tab}
            onChange={(v) => setTab(v)}
            revealKey="exec-focus"
            ariaLabel="Execute focus"
            options={[
              {
                id: "active",
                label: "Active",
                sub: `${inProgress} in progress · ${blockedCount} blocked`,
                icon: <Activity className="h-3.5 w-3.5" />,
              },
              {
                id: "escalations",
                label: "Escalations",
                sub: `${escalations.length} signals`,
                icon: <Siren className="h-3.5 w-3.5" />,
              },
            ]}
          />
          <div className="pt-2">{body}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <CockpitPage

        title="Active Installs"
        maxWidth="7xl"
        controls={headerRight}
        tabs={tabsNode}
      >
        {body}
      </CockpitPage>
      <UnifiedResolutionDrawer />
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

/* Closeout-style flat layout: simplified rows + search + lifecycle chip
 * filter. Row click still opens the JobTriageSheet for deep context. */
const ACTIVE_TEMPLATE = "minmax(220px,1.8fr) 110px 110px 100px 90px 80px";

interface ActiveInstallsTabProps {
  inProgressRows: InstallsLiveRow[];
  blockedRows: InstallsLiveRow[];
  readyRows: InstallsLiveRow[];
  metaByJob: Map<string, RowMeta>;
  now: Date;
  activeIssuesByJob: Map<string, unknown[]>;
  activeTicketsByJob: Map<string, unknown[]>;
  jobsById: Map<string, Job>;
  canOpenChecklist: boolean;
  onReviewChecklist: (jobId: string) => void;
  role: UserRole;
  fieldFeedbackByJob: Map<string, number>;
  substitutionsByJob: Map<string, number>;
}

type LifecycleFilter = "all" | "in_progress" | "blocked" | "ready";

const LIFECYCLE_TONE: Record<
  Lifecycle,
  "good" | "warn" | "bad"
> = {
  in_progress: "good",
  ready: "warn",
  blocked: "bad",
};

const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
  in_progress: "In progress",
  ready: "Ready",
  blocked: "Blocked",
};

const LIFECYCLE_BADGE_CLASS: Record<Lifecycle, string> = {
  in_progress: "bg-success/15 text-success ring-1 ring-success/30",
  ready: "bg-warning/15 text-warning ring-1 ring-warning/30",
  blocked: "bg-destructive/15 text-destructive ring-1 ring-destructive/30",
};

function ActiveInstallsTab(props: ActiveInstallsTabProps) {
  const {
    inProgressRows, blockedRows, readyRows,
    metaByJob, now, activeIssuesByJob, activeTicketsByJob,
    jobsById, canOpenChecklist, onReviewChecklist,
  } = props;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<LifecycleFilter>("all");
  const [triageJobId, setTriageJobId] = useState<string | null>(null);

  // Single flat list, ordered for operator scan: in_progress → blocked → ready.
  const allRows = useMemo(
    () => [...inProgressRows, ...blockedRows, ...readyRows],
    [inProgressRows, blockedRows, readyRows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      const meta = metaByJob.get(r.jobId);
      if (filter !== "all" && meta?.lifecycle !== filter) return false;
      if (!q) return true;
      return (
        r.customerName.toLowerCase().includes(q) ||
        r.jobId.toLowerCase().includes(q) ||
        (r.city ?? "").toLowerCase().includes(q) ||
        (r.installerName ?? "").toLowerCase().includes(q)
      );
    });
  }, [allRows, metaByJob, filter, search]);

  const total = inProgressRows.length + blockedRows.length + readyRows.length;
  if (total === 0) {
    return (
      <CockpitEmpty message="Nothing scheduled for today and no active phase activity." />
    );
  }

  const triageRow = triageJobId
    ? filtered.find((r) => r.jobId === triageJobId) ??
      allRows.find((r) => r.jobId === triageJobId) ?? null
    : null;
  const triageMeta = triageJobId ? metaByJob.get(triageJobId) : undefined;
  const triageJob = triageJobId ? jobsById.get(triageJobId) : undefined;
  const triageBlockers = triageJobId
    ? (activeIssuesByJob.get(triageJobId)?.length ?? 0) +
      (activeTicketsByJob.get(triageJobId)?.length ?? 0)
    : 0;

  return (
    <div className="space-y-3">
      <CockpitFilterBar
        search={
          <CockpitSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by customer, job ID, city, or installer"
          />
        }
        chips={
          <CockpitChipGroup
            value={filter}
            onChange={(v) => setFilter(v as LifecycleFilter)}
            options={[
              { value: "all", label: "All", count: total },
              { value: "in_progress", label: "In progress", count: inProgressRows.length },
              { value: "blocked", label: "Blocked", count: blockedRows.length },
              { value: "ready", label: "Ready", count: readyRows.length },
            ]}
          />
        }
      />

      {filtered.length === 0 ? (
        <CockpitEmpty message="No installs match this search or filter." />
      ) : (
        <CockpitListShell>
          <CockpitColumns template={ACTIVE_TEMPLATE}>
            <span>Job</span>
            <span>Stage</span>
            <span>Phase</span>
            <span>Photos</span>
            <span>Checklist</span>
            <span className="whitespace-nowrap">Updated</span>
          </CockpitColumns>
          {filtered.map((row) => {
            const meta = metaByJob.get(row.jobId);
            const lifecycle: Lifecycle = meta?.lifecycle ?? "ready";
            const issueCount = activeIssuesByJob.get(row.jobId)?.length ?? 0;
            const ticketCount = activeTicketsByJob.get(row.jobId)?.length ?? 0;
            const blockers = issueCount + ticketCount;
            const rowTone: "good" | "warn" | "bad" | "default" =
              lifecycle === "blocked" ? "bad" :
              row.isStale ? "bad" :
              row.activePhaseStatus === "qa_failed" ? "bad" :
              row.awaitingQa || row.reconciliationPending ? "warn" :
              lifecycle === "in_progress" ? "good" :
              "default";
            const phaseLabel =
              lifecycle === "in_progress"
                ? (row.activePhaseName ?? "complete")
                : "Not started";
            return (
              <CockpitRow
                key={row.jobId}
                template={ACTIVE_TEMPLATE}
                onClick={() => setTriageJobId(row.jobId)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusDot tone={rowTone} />
                    <span className="text-sm font-medium text-foreground truncate">
                      {row.customerName}
                    </span>
                    {row.qaFailuresTotal > 0 && (
                      <Badge variant="outline" className="gap-1 text-[10px] text-destructive border-destructive/40 shrink-0">
                        <XCircle className="h-3 w-3" /> {row.qaFailuresTotal}
                      </Badge>
                    )}
                    {blockers > 0 && (
                      <Badge variant="outline" className="gap-1 text-[10px] text-warning border-warning/40 shrink-0">
                        <LifeBuoy className="h-3 w-3" /> {blockers}
                      </Badge>
                    )}
                  </div>
                  <InlineMeta
                    items={[row.jobLabel, row.city, `Installer: ${row.installerName}`]}
                  />
                </div>
                <Badge className={cn("text-[10px] w-fit", LIFECYCLE_BADGE_CLASS[lifecycle])}>
                  {LIFECYCLE_LABEL[lifecycle]}
                </Badge>
                <p className="text-xs text-foreground capitalize truncate">{phaseLabel}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {row.executionAvailable
                    ? `${row.totalPhotosUploaded}/${row.totalPhotosRequired}`
                    : "—"}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {row.executionAvailable ? (
                    canOpenChecklist ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onReviewChecklist(row.jobId);
                        }}
                        className="text-primary hover:underline focus:outline-none focus:underline tabular-nums"
                        aria-label={`Review installer checklist for ${row.customerName}`}
                      >
                        {row.totalChecklistChecked}/{row.totalChecklistTotal} →
                      </button>
                    ) : (
                      <>{row.totalChecklistChecked}/{row.totalChecklistTotal}</>
                    )
                  ) : "—"}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {fmtAge(row.lastActivityAt, now)}
                </p>
              </CockpitRow>
            );
          })}
        </CockpitListShell>
      )}

      <JobTriageSheet
        open={triageRow !== null}
        onOpenChange={(open) => { if (!open) setTriageJobId(null); }}
        row={triageRow}
        job={triageJob}
        lifecycle={triageMeta?.lifecycle}
        blockerReasons={triageMeta?.blockerReasons ?? []}
        blockerLabel={triageMeta?.blockCause?.label}
        blockers={triageBlockers}
      />
    </div>
  );
}



const RISK_TEMPLATE = "minmax(220px,1.8fr) 110px 130px 130px";

function SchedulingRisksTab({
  rows, riskByJob,
}: {
  rows: InstallsLiveRow[];
  riskByJob: Map<string, ReturnType<typeof getJobRiskSignals>>;
}) {
  if (!rows.length) {
    return (
      <CockpitEmpty message="No scheduling risk signals on today's active installs." />
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-muted-foreground">
        In-flight installs carrying reschedules, prior extensions, or conflict history. Source: canonical scheduling event log.
      </div>
      <CockpitListShell>
        <CockpitColumns template={RISK_TEMPLATE}>
          <span>Job</span><span>Reschedules</span><span>Extensions</span><span>Conflicts</span>
        </CockpitColumns>
        {rows.map((row) => {
          const sig = riskByJob.get(row.jobId);
          if (!sig) return null;
          const tone: "good" | "warn" | "bad" | "default" =
            sig.conflicts >= 1 ? "bad" :
            sig.reschedules >= 2 ? "warn" :
            "default";
          return (
            <CockpitRow key={row.jobId} template={RISK_TEMPLATE} to={`/jobs/${row.jobId}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <StatusDot tone={tone} />
                  <span className="text-sm font-medium text-foreground truncate">{row.customerName}</span>
                </div>
                <InlineMeta items={[row.jobLabel, row.city, `Installer: ${row.installerName}`]} />
              </div>
              <p className="text-xs text-foreground tabular-nums">
                {sig.reschedules >= 1 ? <span className="inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" />{sig.reschedules}×</span> : "—"}
              </p>
              <p className="text-xs text-foreground tabular-nums">
                {sig.extensions >= 1 ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{sig.extensions}</span> : "—"}
              </p>
              <p className="text-xs text-foreground tabular-nums">
                {sig.conflicts >= 1 ? <span className="inline-flex items-center gap-1 text-warning"><AlertTriangle className="h-3 w-3" />{sig.conflicts}</span> : "—"}
              </p>
            </CockpitRow>
          );
        })}
      </CockpitListShell>
    </div>
  );
}

const BLOCK_TEMPLATE = "minmax(220px,1.8fr) 90px 90px 110px";

function BlockingIssuesTab({ items }: { items: Array<{ row: InstallsLiveRow; issues: unknown[]; tickets: unknown[] }> }) {
  if (!items.length) {
    return (
      <CockpitEmpty message="No blocking issues or service tickets on today's active installs." />
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-muted-foreground">
        In-flight installs with one or more open issues or service tickets. Grouped by job for fast triage.
      </div>
      <CockpitListShell>
        <CockpitColumns template={BLOCK_TEMPLATE}>
          <span>Job</span><span>Issues</span><span>Tickets</span><span>Total</span>
        </CockpitColumns>
        {items.map(({ row, issues: js, tickets: ts }) => {
          const total = js.length + ts.length;
          const tone: "good" | "warn" | "bad" | "default" = total >= 3 ? "bad" : "warn";
          return (
            <CockpitRow key={row.jobId} template={BLOCK_TEMPLATE} to={`/jobs/${row.jobId}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <StatusDot tone={tone} />
                  <span className="text-sm font-medium text-foreground truncate">{row.customerName}</span>
                </div>
                <InlineMeta items={[row.jobLabel, `Installer: ${row.installerName}`]} />
              </div>
              <p className="text-xs text-foreground tabular-nums">
                {js.length > 0 ? <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{js.length}</span> : "—"}
              </p>
              <p className="text-xs text-foreground tabular-nums">
                {ts.length > 0 ? <span className="inline-flex items-center gap-1"><LifeBuoy className="h-3 w-3" />{ts.length}</span> : "—"}
              </p>
              <p className="text-xs font-semibold text-foreground tabular-nums">{total}</p>
            </CockpitRow>
          );
        })}
      </CockpitListShell>
    </div>
  );
}

function EscalationsTab({ signals }: { signals: EscalationSignal[] }) {
  if (!signals.length) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        No escalation clusters detected. Cluster signals require at least 3 related rows before they surface.
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-muted-foreground">
        Cross-signal clusters derived from the active board. Pure read-only derivation — no automation, no notifications.
      </div>
      {signals.map((s) => (
        <Card key={s.id} className="p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {s.kind === "installer_overload" && <Users className="h-4 w-4 text-muted-foreground shrink-0" />}
                {s.kind === "qa_backlog" && <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />}
                {s.kind === "stalled_cluster" && <Clock className="h-4 w-4 text-muted-foreground shrink-0" />}
                {s.kind === "risk_concentration" && <Layers className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className="font-semibold text-sm">{s.headline}</span>
                <Badge className={cn("text-[10px] capitalize", severityClass(s.severity))}>
                  {s.severity}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {s.jobIds.length} job{s.jobIds.length === 1 ? "" : "s"}
                </Badge>
              </div>
              <div className="mt-1 text-[12px] text-muted-foreground">{s.detail}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
